// src/api/dataRoom.ts
//
// Client Data Room API wrapper. JSON calls go through clientService (so they
// get the bearer token + refresh handling); the actual file bytes are PUT
// directly to GCS via a resumable session URI (bypassing our backend).

import clientService from 'src/clientService';
import { ContentType } from 'src/api/Api';

const API_PREFIX = '/api/v1';

export type NodeType = 'folder' | 'file';
export type Branch = 'custom' | 'deployment';
export type Scope = 'project' | 'site';

export interface TreeNode {
  id: string;
  name: string;
  node_type: NodeType;
  branch: Branch;
  editable: boolean;
  content_type?: string | null;
  size_bytes?: number | null;
  gcs_path?: string | null;
  updated_at?: string | null;
  scope?: Scope | null;
  site_id?: string | null;
  parent_id?: string | null;
  dep_parent?: string | null;
}

export interface TreeResponse {
  project_id: string;
  nodes: TreeNode[];
}

export interface ClientNode {
  id: string;
  project_id: string;
  scope: Scope;
  site_id?: string | null;
  parent_id?: string | null;
  node_type: NodeType;
  name: string;
  gcs_path?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  uploaded_by_user_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface DeploymentFile {
  name: string;
  gcs_path: string;
  size_bytes: number | null;
  content_type: string | null;
  updated_at: string | null;
}

interface InitiateUploadResponse {
  upload_session_uri: string;
  gcs_path: string;
}

async function request<T>(
  path: string,
  method: 'GET' | 'POST' | 'DELETE',
  body?: unknown,
  query?: Record<string, string | undefined>,
): Promise<T> {
  // clientService.request handles auth + token refresh. It expects a path
  // relative to baseUrl and a body under `body`.
  const res = await clientService.request<T, { detail?: string }>({
    path: `${API_PREFIX}${path}`,
    method,
    body: body as any,
    query: query as any,
    secure: true,
    format: 'json',
    type: body ? ContentType.Json : undefined,
  });
  return res.data;
}

export const dataRoomApi = {
  getTree(projectId: string): Promise<TreeResponse> {
    return request<TreeResponse>(`/projects/${projectId}/data-room/tree`, 'GET');
  },

  listDeploymentFiles(
    projectId: string,
    deploymentId: string,
    folder: 'audios' | 'images',
  ): Promise<{ deployment_id: string; folder: string; files: DeploymentFile[] }> {
    return request(
      `/projects/${projectId}/data-room/deployment-files`,
      'GET',
      undefined,
      { deployment_id: deploymentId, folder },
    );
  },

  createFolder(
    projectId: string,
    payload: { name: string; scope: Scope; site_id?: string; parent_id?: string },
  ): Promise<ClientNode> {
    return request<ClientNode>(
      `/projects/${projectId}/data-room/folders`,
      'POST',
      payload,
    );
  },

  deleteNode(projectId: string, nodeId: string): Promise<void> {
    return request<void>(
      `/projects/${projectId}/data-room/nodes/${nodeId}`,
      'DELETE',
    );
  },

  getDownloadUrl(projectId: string, gcsPath: string): Promise<{ download_url: string }> {
    return request(
      `/projects/${projectId}/data-room/download-url`,
      'GET',
      undefined,
      { gcs_path: gcsPath },
    );
  },

  async initiateUpload(
    projectId: string,
    payload: {
      file_name: string;
      content_type: string;
      scope: Scope;
      site_id?: string;
      parent_id?: string;
    },
  ): Promise<InitiateUploadResponse> {
    return request<InitiateUploadResponse>(
      `/projects/${projectId}/data-room/uploads/initiate`,
      'POST',
      payload,
    );
  },

  finalizeUpload(
    projectId: string,
    payload: {
      file_name: string;
      gcs_path: string;
      content_type?: string;
      scope: Scope;
      site_id?: string;
      parent_id?: string;
    },
  ): Promise<ClientNode> {
    return request<ClientNode>(
      `/projects/${projectId}/data-room/uploads/finalize`,
      'POST',
      payload,
    );
  },
};

/**
 * Upload a File to a GCS resumable session URI with progress.
 * Single PUT of the whole body; GCS resumable sessions accept this and it
 * keeps the client simple while still supporting very large files.
 */
export function uploadToSession(
  sessionUri: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', sessionUri, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      // GCS returns 200 or 201 on a completed resumable upload.
      if (xhr.status === 200 || xhr.status === 201) {
        resolve();
      } else {
        reject(new Error(`Upload failed (${xhr.status}): ${xhr.responseText}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}
